import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Clock, X, Youtube, Loader2,
} from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const TZS = ["UTC", "Europe/Paris", "Europe/London", "America/New_York",
  "America/Los_Angeles", "Asia/Baku", "Asia/Tokyo", "Australia/Sydney"];

function startOfGrid(year, month) {
  const first = new Date(Date.UTC(year, month, 1));
  const dow = (first.getUTCDay() + 6) % 7; // Monday-first
  return new Date(Date.UTC(year, month, 1 - dow));
}

// 6-week (42-cell) grid of days for the given month.
function buildMonthGrid(year, month) {
  const gridStart = startOfGrid(year, month);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setUTCDate(gridStart.getUTCDate() + i);
    return d;
  });
}

// Stable date-cell key: YYYY-M-D (UTC).
function dayKey(d) {
  return `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
}

function ScheduleDialog({ approvedProjects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [projectId, setProjectId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [tz, setTz] = useState("UTC");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!projectId || !date) { toast.error("Pick a project and a date"); return; }
    setBusy(true);
    try {
      const iso = new Date(`${date}T${time}:00`).toISOString();
      await api.post("/calendar", { project_id: projectId, scheduled_at: iso, timezone: tz, note });
      toast.success("Scheduled");
      setOpen(false); setProjectId(""); setDate(""); setNote("");
      onCreated();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="open-schedule-dialog">
          <Plus className="w-4 h-4 mr-1.5" /> Schedule a video
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
        <DialogHeader>
          <DialogTitle>Schedule a video</DialogTitle>
          <DialogDescription className="text-slate-500">Pick an approved project and a date/time. Internal schedule — independent of YouTube.</DialogDescription>
        </DialogHeader>
        {approvedProjects.length === 0 ? (
          <p className="text-sm text-slate-400 py-4">No approved projects on this channel yet. Approve a render first — scheduling is independent of YouTube.</p>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block">Approved project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger className="bg-slate-900 border-slate-800 text-slate-100" data-testid="schedule-project-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                  {approvedProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-slate-900 border-slate-800 text-slate-100" data-testid="schedule-date" /></div>
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Time</Label><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="bg-slate-900 border-slate-800 text-slate-100" data-testid="schedule-time" /></div>
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block">Timezone</Label>
              <Select value={tz} onValueChange={setTz}>
                <SelectTrigger className="bg-slate-900 border-slate-800 text-slate-100" data-testid="schedule-tz"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                  {TZS.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-300 text-sm mb-1.5 block">Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="bg-slate-900 border-slate-800 text-slate-100" /></div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={submit} disabled={busy || approvedProjects.length === 0} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="submit-schedule">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Calendar() {
  const { selectedChannelId, channels } = useStudio();
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth());
  const [items, setItems] = useState(null);
  const [approved, setApproved] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const cq = selectedChannelId ? `?channel_id=${selectedChannelId}` : "";
      const [cal, projs] = await Promise.all([
        api.get(`/calendar${cq}`),
        selectedChannelId ? api.get(`/projects?channel_id=${selectedChannelId}`) : Promise.resolve({ data: [] }),
      ]);
      setItems(cal.data);
      setApproved(projs.data.filter((p) => ["approved", "scheduled"].includes(p.stage) && p.approval));
    } catch (e) { setError(apiError(e)); }
  }, [selectedChannelId]);
  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    try { await api.post(`/calendar/${id}/cancel`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const byDay = useMemo(() => {
    const map = {};
    (items || []).forEach((it) => {
      const d = new Date(it.scheduled_at);
      const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
      (map[key] = map[key] || []).push(it);
    });
    return map;
  }, [items]);

  const days = buildMonthGrid(year, month);
  const channelName = channels.find((c) => c.id === selectedChannelId)?.data?.name || "All channels";
  const upcoming = (items || []).filter((i) => i.status === "scheduled" && new Date(i.scheduled_at) >= new Date())
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!items) return <LoadingState label="Loading calendar…" />;

  return (
    <div className="animate-in-up">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1">Production</p>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Publishing Calendar</h1>
          <p className="text-sm text-slate-400 mt-2">Internal schedule for <span className="text-slate-200">{channelName}</span>. Works without YouTube — connect YouTube to also publish.</p>
        </div>
        <ScheduleDialog approvedProjects={approved} onCreated={load} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-[#111827] border border-slate-800/80 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => { const m = month - 1; if (m < 0) { setMonth(11); setYear(year - 1); } else setMonth(m); }} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-800" data-testid="cal-prev"><ChevronLeft className="w-5 h-5" /></button>
            <h2 className="text-lg font-display font-semibold text-slate-100" data-testid="cal-title">{MONTHS[month]} {year}</h2>
            <button onClick={() => { const m = month + 1; if (m > 11) { setMonth(0); setYear(year + 1); } else setMonth(m); }} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-800" data-testid="cal-next"><ChevronRight className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase tracking-wide text-slate-500 mb-1">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d, i) => {
              const inMonth = d.getUTCMonth() === month;
              const key = dayKey(d);
              const dayItems = byDay[key] || [];
              const isToday = d.toDateString() === new Date().toDateString();
              return (
                <div key={key} className={`min-h-[74px] rounded-lg border p-1.5 text-left ${inMonth ? "border-slate-800 bg-slate-900/40" : "border-slate-900 bg-slate-900/10 opacity-50"} ${isToday ? "ring-1 ring-violet-500/50" : ""}`} data-testid={`cal-day-${key}`}>
                  <div className="text-[11px] text-slate-500 mb-1">{d.getUTCDate()}</div>
                  {dayItems.map((it) => (
                    <button key={it.id} onClick={() => navigate(`/projects/${it.project_id}?tab=publish`)}
                      className={`w-full truncate text-left text-[10px] px-1.5 py-0.5 rounded mb-0.5 ${it.status === "published" ? "bg-emerald-500/15 text-emerald-300" : "bg-violet-500/15 text-violet-200 hover:bg-violet-500/25"}`}
                      title={it.project_title} data-testid={`cal-item-${it.id}`}>
                      {new Date(it.scheduled_at).toUTCString().slice(17, 22)} {it.project_title}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-4" data-testid="upcoming-panel">
          <h3 className="font-display font-semibold text-slate-100 mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-violet-300" /> Upcoming</h3>
          {upcoming.length === 0 ? (
            <EmptyState icon={CalendarDays} title="Nothing scheduled" description="Schedule an approved video to see it here." />
          ) : (
            <div className="space-y-2" data-testid="upcoming-list">
              {upcoming.map((it) => (
                <div key={it.id} className="border border-slate-800 rounded-lg p-3" data-testid={`upcoming-${it.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <button onClick={() => navigate(`/projects/${it.project_id}?tab=publish`)} className="text-sm text-slate-200 text-left hover:text-violet-300">{it.project_title}</button>
                    <button onClick={() => cancel(it.id)} className="text-slate-500 hover:text-rose-400" data-testid={`cancel-schedule-${it.id}`}><X className="w-4 h-4" /></button>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {new Date(it.scheduled_at).toLocaleString()} · <span className="text-slate-400">{it.timezone}</span>
                  </div>
                  {it.note && <div className="text-xs text-slate-500 mt-1 italic">{it.note}</div>}
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500 border-t border-slate-800 pt-3">
            <Youtube className="w-3.5 h-3.5" /> Scheduling here is internal. YouTube publishing happens from a project's Publish tab.
          </div>
        </div>
      </div>
    </div>
  );
}
